import { useMemo, useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, isSameDay } from "date-fns";
import { MapPin, Video, Home, Building2, Leaf, Sun, Monitor } from "lucide-react";
import type { BookingData } from "../BookingModal";
import { useCalendarLocationSymbols } from "@/hooks/booking/useCalendarLocationSymbols";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

interface DateSelectionProps {
  bookingData: BookingData;
  setBookingData: (data: BookingData) => void;
  onNext: () => void;
}

interface LocationInfo {
  id: string;
  name: string;
  city: string | null;
  symbol: React.ReactNode;
  symbolClass: string;
}

// Symboles pour les lieux (au lieu des couleurs)
const LOCATION_SYMBOLS = [
  { icon: Home, class: "text-primary" },
  { icon: Building2, class: "text-secondary" },
  { icon: Leaf, class: "text-accent" },
  { icon: Sun, class: "text-muted-foreground" },
  { icon: MapPin, class: "text-foreground" },
];

export function DateSelection({ bookingData, setBookingData, onNext }: DateSelectionProps) {
  const [bookedDates, setBookedDates] = useState<Date[]>([]);
  const [visibleMonth, setVisibleMonth] = useState<Date>(bookingData.date ?? new Date());

  const {
    loading: loadingLocations,
    locations: rawLocations,
    dateMap,
    prefetchMonth,
  } = useCalendarLocationSymbols(true); // Always load to support both modes

  const locations: LocationInfo[] = useMemo(() => {
    return rawLocations.map((loc) => {
      const symbolConfig = LOCATION_SYMBOLS[loc.symbolIndex % LOCATION_SYMBOLS.length];
      const IconComponent = symbolConfig.icon;
      return {
        id: loc.id,
        name: loc.name,
        city: loc.city,
        symbol: <IconComponent className={`w-4 h-4 ${symbolConfig.class}`} />,
        symbolClass: symbolConfig.class,
      };
    });
  }, [rawLocations]);

  useEffect(() => {
    loadBookedDates();
    prefetchMonth(visibleMonth);
  }, []);

  useEffect(() => {
    prefetchMonth(visibleMonth);
  }, [visibleMonth, prefetchMonth]);

  const loadBookedDates = async () => {
    // Fetch all appointments to find fully booked dates
    const { data, error } = await supabase
      .from("appointments")
      .select("appointment_date, appointment_time")
      .gte("appointment_date", startOfDay(new Date()).toISOString());

    if (error) {
      console.error("Error loading booked dates:", error);
      return;
    }

    // Group by date and count appointments
    const dateCount: { [key: string]: number } = {};
    data?.forEach((apt) => {
      const date = new Date(apt.appointment_date).toDateString();
      dateCount[date] = (dateCount[date] || 0) + 1;
    });

    // Dates with 8+ appointments are considered fully booked
    const fullyBooked = Object.entries(dateCount)
      .filter(([_, count]) => count >= 8)
      .map(([date]) => new Date(date));

    setBookedDates(fullyBooked);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setBookingData({ ...bookingData, date, time: null });
      onNext();
    }
  };

  const isDateDisabled = (date: Date) => {
    // Disable past dates
    if (date < startOfDay(new Date())) return true;
    
    // Disable fully booked dates
    return bookedDates.some(bookedDate => isSameDay(date, bookedDate));
  };

  const isDateSelectable = (date: Date) => {
    if (isDateDisabled(date)) return false;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const info = dateMap.get(key);
    if (!info) return false;
    return bookingData.isOnline ? info.hasVisio : info.locationIds.length > 0;
  };

  // Get location symbols for a specific date - filtered by booking mode
  // If booking isOnline (visio): show only visio symbol
  // If booking in présentiel (!isOnline): show only location symbols
  const getDateSymbols = (date: Date) => {
    if (isDateDisabled(date)) return null;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const info = dateMap.get(key);
    if (!info) return null;

    const symbols: React.ReactNode[] = [];
    
    // Filter based on booking mode
    if (bookingData.isOnline) {
      // Visio mode: show only visio symbol
      if (info.hasVisio) {
        symbols.push(
          <span key="visio" className="inline-flex" title="Visio disponible">
            <Monitor className="w-4 h-4 text-blue-500" />
          </span>
        );
      }
    } else {
      // Présentiel mode: show only location symbols
      for (const locationId of info.locationIds) {
        const location = locations.find((l) => l.id === locationId);
        if (!location) continue;
        symbols.push(
          <span key={locationId} className="inline-flex" title={location.name + (location.city ? ` · ${location.city}` : "")}>
            {location.symbol}
          </span>
        );
      }
    }

    return symbols.length ? symbols : null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Sélectionnez la date et l'heure</h2>
        <p className="text-muted-foreground">
          Choisissez un jour disponible dans le calendrier
        </p>
      </div>

      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={bookingData.date || undefined}
          onSelect={handleDateSelect}
          locale={fr}
          disabled={(date) => !isDateSelectable(date)}
          className="rounded-md border"
          classNames={{
            // Augmenter la taille des cellules pour afficher les symboles
            cell: "h-11 w-11 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day: cn(buttonVariants({ variant: "ghost" }), "h-11 w-11 p-0 font-normal aria-selected:opacity-100"),
            head_cell: "text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]",
          }}
          modifiers={{
            available: (date) => {
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
              const info = dateMap.get(key);
              if (!info) return false;
              if (isDateDisabled(date)) return false;

              // Strict filtering by mode:
              // - Visio: only days with visio availability
              // - Présentiel: only days with at least one cabinet location
              if (bookingData.isOnline) return info.hasVisio;
              return info.locationIds.length > 0;
            }
          }}
          modifiersClassNames={{
            available: "font-semibold"
          }}
          onMonthChange={(month) => setVisibleMonth(month)}
          components={{
            DayContent: ({ date }) => {
              const symbols = getDateSymbols(date);
              const hasAvailability = isDateSelectable(date);
              
              return (
                <div className="relative flex flex-col items-center justify-center w-full h-full pt-0.5">
                  <span className="text-sm">{date.getDate()}</span>
                  {/* Afficher les symboles (filtrés par mode) */}
                  {symbols && symbols.length > 0 && hasAvailability && (
                    <div className="flex gap-0 mt-0.5">
                      {symbols.slice(0, 4).map((sym, idx) => (
                        <span key={idx} className="scale-[0.5] -mx-1">{sym}</span>
                      ))}
                      {symbols.length > 4 && (
                        <span className="text-[6px] text-muted-foreground">+{symbols.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            }
          }}
        />
      </div>

      {/* Légende des symboles - filtered by booking mode */}
      {!loadingLocations && !bookingData.isOnline && locations.length > 0 && (
        <div className="bg-accent/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Légende des lieux de consultation</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {/* Symboles des lieux uniquement pour mode présentiel */}
            {locations.map((location) => (
              <div key={location.id} className="flex items-center gap-2">
                {location.symbol}
                <span className="text-sm text-muted-foreground">
                  {location.name}{location.city ? ` · ${location.city}` : ""}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 italic">
            Les symboles sur le calendrier indiquent les lieux de consultation disponibles chaque jour
          </p>
        </div>
      )}

      {/* Indication pour visio */}
      {bookingData.isOnline && (
        <div className="bg-accent/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Monitor className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-primary">Consultation en visio</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Monitor className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-muted-foreground">Créneau visio disponible</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2 italic">
            Un lien de visioconférence vous sera envoyé par email après confirmation
          </p>
        </div>
      )}

      <div className="text-sm text-muted-foreground text-center">
        <p>Fuseau horaire</p>
        <p className="font-medium">Heure d'Europe centrale (UTC+1)</p>
      </div>
    </div>
  );
}
